<template>
  <div class="assign-user-popover" @click.stop>
    <div class="popover-content p-3 position-relative">
      <!-- Close -->
      <div class="toolbar-close-icon" @click="$emit('close')">
        <svg
          id="Icon_ionic-md-close-circle-outline"
          data-name="Icon ionic-md-close-circle-outline"
          xmlns="http://www.w3.org/2000/svg"
          width="16.062"
          height="16.062"
          viewBox="0 0 16.062 16.062"
        >
          <path
            id="Path_10189"
            data-name="Path 10189"
            d="M11.406,5A6.406,6.406,0,1,1,6.873,6.873,6.383,6.383,0,0,1,11.406,5m0-1.622a8.031,8.031,0,1,0,8.031,8.031,8.03,8.03,0,0,0-8.031-8.031Z"
            transform="translate(-3.375 -3.375)"
            fill="#204c90"
          />
          <path
            id="Path_10190"
            data-name="Path 10190"
            d="M18.718,17.595l-1.124,1.124L14.7,15.826l-2.892,2.892-1.124-1.124L13.579,14.7l-2.892-2.892,1.124-1.124L14.7,13.579l2.892-2.892,1.124,1.124L15.826,14.7Z"
            transform="translate(-6.672 -6.672)"
            fill="#204c90"
          />
        </svg>
      </div>

      <!-- Start Date -->
      <label for="startDate">Start Date</label>
      <b-form-datepicker
        v-model="form.startDate"
        size="sm"
        class="mb-2"
        :min="today"
      />
      <b-form-checkbox v-model="form.deadlineEnabled">
        Deadline
      </b-form-checkbox>
      <b-form-datepicker
        v-if="form.deadlineEnabled"
        v-model="form.deadline"
        size="sm"
        class="mb-2"
        :min="today"
      />

      <!-- PDF Copy -->
      <b-form-checkbox v-model="form.includePdf">
        Receive responses from users
      </b-form-checkbox>

      <label for="assignment-number-of-attempts" class="mt-2">Number of Attempts</label>
      <b-form-input
        id="assignment-number-of-attempts"
        v-model.number="form.numberOfAttempts"
        type="number"
        min="1"
        max="2147483647"
        step="1"
        size="sm"
        :state="validNumberOfAttempts ? null : false"
      />
      <b-form-invalid-feedback :state="validNumberOfAttempts">
        Enter a positive whole number.
      </b-form-invalid-feedback>

      <hr />

      <!-- New Users -->
      <label class="font-weight-bold">
        Provide a comma separated list of emails
      </label>

      <b-form-textarea
        v-model="form.emails"
        rows="3"
        placeholder="user1@email.com, user2@email.com"
      />

      <b-button
        size="sm"
        variant="primary"
        class="mt-2"
        :disabled="!validNumberOfAttempts"
        @click="sendToNewUsers"
      >
        {{ isEditMode ? "Send to new Users" : "Send to Users" }}
      </b-button>

      <!-- Existing Recipients -->
      <div v-if="isEditMode && form.existingRecipients.length">
        <hr />
        <label class="font-weight-bold"> Current Recipients: </label>

        <div class="recipient-list" role="listbox" aria-multiselectable="true">
          <div
            v-for="email in form.existingRecipients"
            :key="email"
            class="recipient-item"
            :class="{ selected: form.selectedRecipients.includes(email) }"
            @click="toggleRecipient(email)"
          >
            {{ email }}
          </div>
        </div>

        <b-button
          size="sm"
          variant="primary"
          class="mt-2"
          :disabled="!form.selectedRecipients.length || !validNumberOfAttempts"
          @click="resendToExisting"
        >
          Resent to selected users
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "assign-to-user-form",
  props: {
    assignment: {
      type: Object,
      default: null,
    },
  },
  data() {
    const today = new Date().toISOString().split('T')[0];
    return {
      form: {
        deadlineEnabled: false,
        startDate: today,
        deadline: null,
        includePdf: false,
        numberOfAttempts: 1,
        emails: "",
        existingRecipients: [],
        selectedRecipients: [],
      },
      today: today,
    };
  },
  computed: {
    validNumberOfAttempts() {
      return Number.isInteger(this.form.numberOfAttempts) &&
        this.form.numberOfAttempts >= 1 && this.form.numberOfAttempts <= 2147483647;
    },
    isEditMode() {
      return !!this.assignment;
    },
    existingRecipientOptions() {
      return this.form.existingRecipients.map((e) => ({
        text: e,
        value: e,
      }));
    },
  },
  watch: {
    assignment: {
      immediate: true,
      handler(val) {
        if (!val) return;

        this.form.deadlineEnabled = !!val.deadlineDate;
        this.form.deadline = val.deadlineDate
          ? val.deadlineDate.split(" ")[0]
          : null;
        this.form.includePdf = !!val.receivePdfCopy;
        this.form.numberOfAttempts = val.numberOfAttempts || 1;
        this.form.existingRecipients = val.recipientEmails || [];
        this.form.emails = "";
      },
    },
  },
  methods: {
    toggleRecipient(email) {
      const idx = this.form.selectedRecipients.indexOf(email);
      if (idx === -1) {
        this.form.selectedRecipients.push(email);
      } else {
        this.form.selectedRecipients.splice(idx, 1);
      }
    },

    async sendToNewUsers() {
      if (!this.validNumberOfAttempts) return;
      const emails = this.form.emails
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean);

      if (!emails.length) return;

      const payload = {
        deadline: this.form.deadlineEnabled ? this.form.deadline : null,
        includePdf: this.form.includePdf,
        numberOfAttempts: this.form.numberOfAttempts,
        emails: emails,
        startDate: this.form.startDate,
       
      };
      if (this.isEditMode) {
        payload.id = this.assignment?.materialId;
        payload.tumorSiteId = this.assignment?.tumorSiteId;
        payload.tumorSiteName = this.assignment?.tumorSiteName;
        payload.isShowPublicSite = this.assignment?.isShowPublicSite;
        payload.type = this.assignment?.materialType;
        payload.isEditMode = this.isEditMode;
      }
      this.$emit("assign", payload);
      
      this.form.emails = "";
    },

    async resendToExisting() {
      if (!this.validNumberOfAttempts) return;
      if (!this.form.selectedRecipients.length) return;

      if (this.isEditMode) {
        this.$emit("resend-existing", {
          assignmentId: this.assignment.assignmentId,
          materialId: this.assignment.materialId,
          emails: this.form.selectedRecipients,
          deadline: this.form.deadlineEnabled ? this.form.deadline : null,
          includePdf: this.form.includePdf,
          numberOfAttempts: this.form.numberOfAttempts,
          randomId: this.assignment.randomId,
        });
      }

      this.form.selectedRecipients = [];
    },
  },
};
</script>
<style scoped>
.recipient-list {
  border: 1px solid #ccc;
  max-height: 120px;
  overflow-y: auto;
  padding: 2px;
  background: #fff;
}

.recipient-item {
  padding: 6px 8px;
  cursor: pointer;
  font-size: 13px;
  user-select: none;
}

.recipient-item:hover {
  background: #f2f6ff;
}

.recipient-item.selected {
  background: #2b6cb0;
  color: #fff;
}
</style>
